import { FC, ReactNode, useMemo } from 'react';
import { expand } from 'uri-template-lite';
import {
  formatDate,
  formatDateTimeShort,
  getUserName,
  InputProcessingErrorExcelUrl,
  ProcessingEventReport,
  Tooltip,
  useInputProcessingEventQuery,
  useSystemClassifierItemById,
  useSystemClassifierItemsBy,
} from '@atlas-frontend/sdk';
import { useNumericParam } from '../../../common/useEntityIdParam';
import { InputDataProcessingEventReportTable } from './InputDataProcessingEventReportTable';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataProcessingEventReport.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-processing-event-report',
);
addTranslations(translations);

export const InputDataProcessingEventReport: FC = () => {
  const processingEventId = useNumericParam('eventId');
  const { t } = useTranslation();
  const { data, isLoading } = useInputProcessingEventQuery(
    processingEventId || 0,
    {
      enabled: Boolean(processingEventId || 0),
      throwOnError: true,
    },
  );

  const processingMethod = useSystemClassifierItemById(
    'PROCESSING_METHOD',
    data?.processingEvent.processingMethodId,
  );

  const reportType = useMemo(
    () => (processingMethod?.code === 'FILE' ? 'from_file' : 'normal'),
    [processingMethod],
  );

  const title = useMemo(
    () =>
      (data &&
        (reportType === 'normal'
          ? data.processingEvent.dataObjectCodeName
          : reportType === 'from_file'
            ? data.processingBatches[0]?.fileName
            : t('unknown-error'))) ||
      '',
    [reportType, data, t],
  );

  const systemClassifierItemsBy = useSystemClassifierItemsBy([
    'PROCESSING_TYPE',
    'DATA_PROCESSING_SCOPE',
    'PROCESSING_METHOD',
    'PROCESSING_ERROR_LEVEL',
    'PROCESSING_STATUS',
  ]);

  const meta = useMemo(() => {
    if (!data) {
      return [];
    }

    const {
      valueDate,
      startTime,
      endTime,
      createdBy,
      processingTypeId,
      processingMethodId,
      dataProcessingScopeId,
      errorLevelId,
      statusId,
    } = data.processingEvent;

    const meta: { label: string; value: ReactNode }[] = [];

    if (reportType === 'normal') {
      meta.push({
        label: t('field.valueDate'),
        value: formatDate(valueDate),
      });
    }

    meta.push(
      {
        label: t('field.startTime'),
        value: formatDateTimeShort(startTime),
      },
      { label: t('field.endTime'), value: formatDateTimeShort(endTime) },
      {
        label: t('field.createdBy'),
        value: (
          <Tooltip title={getUserName(createdBy)}>{createdBy.username}</Tooltip>
        ),
      },
      {
        label: t('field.processingType'),
        value:
          systemClassifierItemsBy.PROCESSING_TYPE.get(processingTypeId)?.name,
      },
      {
        label: t('field.dataProcessingScope'),
        value: systemClassifierItemsBy.DATA_PROCESSING_SCOPE.get(
          dataProcessingScopeId || 0,
        )?.name,
      },
      {
        label: t('field.processingMethod'),
        value:
          systemClassifierItemsBy.PROCESSING_METHOD.get(processingMethodId)
            ?.name,
      },
      {
        label: t('field.errorLevel'),
        value: systemClassifierItemsBy.PROCESSING_ERROR_LEVEL.get(
          errorLevelId || 0,
        )?.name,
      },
      {
        label: t('field.status'),
        value:
          statusId &&
          systemClassifierItemsBy.PROCESSING_STATUS.get(statusId)?.name,
      },
    );

    return meta;
  }, [t, data, reportType, systemClassifierItemsBy]);

  return (
    <ProcessingEventReport
      title={title}
      meta={meta}
      downloadUrl={expand(InputProcessingErrorExcelUrl, {
        id: processingEventId,
      })}
      count={data?.processingErrors.length}
      isLoading={isLoading}
    >
      {data && (
        <InputDataProcessingEventReportTable processingEventDetails={data} />
      )}
    </ProcessingEventReport>
  );
};
